/**
 * Firebase client setup ISOLATO del portale paziente.
 *
 * VINCOLO INVIOLABILE (PR4 brief):
 *  - NESSUN import di `firebase/auth`
 *  - NESSUN `getAuth()` chiamato
 *  - Il portale paziente e' ANONIMO: ogni Cloud Function chiamata
 *    qui e' marcata "no auth" lato backend e validata via UUID
 *    token + rate limiting.
 *
 * Carico solo:
 *  - initializeApp (Firebase core)
 *  - getDocumentiLegaliAttivi e completeOnboarding
 *  - AppCheck (verifica integrità del client; senza, le CF
 *    rifiutano la chiamata se configurate enforceAppCheck:true)
 *
 * Sicurezza:
 *  - Le env VITE_FIREBASE_* sono replicate da .env (stessa config
 *    del gestionale ma senza inizializzare auth/firestore/storage).
 *  - In dev (emulator) connettiamo solo Functions.
 */

import { initializeApp, getApps, type FirebaseApp } from 'firebase/app';
import {
  getFunctions,
  connectFunctionsEmulator,
  type Functions,
} from 'firebase/functions';
import {
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
  type AppCheck,
} from 'firebase/app-check';

declare global {
  // Firebase App Check espone questa global in dev per attivare il debug token.
  var FIREBASE_APPCHECK_DEBUG_TOKEN: boolean | string | undefined;
}

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY ?? 'demo-key',
  authDomain:
    import.meta.env.VITE_FIREBASE_AUTH_DOMAIN ?? 'demo-dietagest.firebaseapp.com',
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID ?? 'demo-dietagest',
  storageBucket:
    import.meta.env.VITE_FIREBASE_STORAGE_BUCKET ?? 'demo-dietagest.appspot.com',
  messagingSenderId:
    import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID ?? '000000000000',
  appId: import.meta.env.VITE_FIREBASE_APP_ID ?? '1:000000000000:web:demo',
};

export const pazienteApp: FirebaseApp =
  getApps()[0] ?? initializeApp(firebaseConfig);

export const pazienteFunctions: Functions = getFunctions(
  pazienteApp,
  'europe-west8'
);

let appCheck: AppCheck | null = null;
export function initPazienteAppCheck(): void {
  if (appCheck) return;

  if (import.meta.env.DEV && typeof globalThis !== 'undefined') {
    globalThis.FIREBASE_APPCHECK_DEBUG_TOKEN = true;
  }

  // Accetta sia VITE_RECAPTCHA_ENTERPRISE_KEY (canonica) sia
  // VITE_APPCHECK_RECAPTCHA_KEY (alias storico) per retrocompatibilità.
  const siteKey =
    import.meta.env.VITE_RECAPTCHA_ENTERPRISE_KEY ??
    import.meta.env.VITE_APPCHECK_RECAPTCHA_KEY;
  if (!siteKey) return;
  try {
    appCheck = initializeAppCheck(pazienteApp, {
      provider: new ReCaptchaEnterpriseProvider(siteKey),
      isTokenAutoRefreshEnabled: true,
    });
  } catch {
    // Silenzioso in dev se non configurato; CF saranno comunque
    // rifiutate da enforceAppCheck:true ma il portale non crasha.
  }
}

if (import.meta.env.DEV) {
  // Connessione emulator Functions su porta default 5001.
  connectFunctionsEmulator(pazienteFunctions, '127.0.0.1', 5001);
}
